<script setup lang="ts">
import {API_URL, parseEloCredentialsError} from "../ts/utils";
import PasswordRules from "./PasswordRules.vue";
import {ref} from "vue";
import {$t} from "../ts/translations";
import {BField} from "buefy";
import PasswordStrength from "./PasswordStrength.vue";

/**
 * This component can appear in three different contexts:
 * - required as part of the login process (not yet authenticated)
 *  => invokes /login-and-change-password
 *  => this also requires credentials, including timezone and selected language
 * - optional as part of the login process (already authenticated)
 *  => old password provided from login form
 *  => invokes /change-password
 * - as standalone page (already authenticated)
 * => requires old password input
 * => invokes /change-password
 *
 * Due to the versatility in which contexts it appears, it has the following logic:
 * - `passwordRules` must be provided, otherwise they will not be shown
 * - if `oldPassword` is provided (by the login form), the field is hidden
 * - a `password-changed` event is raised in case of success
 * - in case the user is not yet authenticated, the default `changePasswordFunction` can be overwritten
 */
const props = defineProps(['oldPassword', 'passwordRules', 'changePasswordFunction'])

const emit = defineEmits(['password-changed'])

const oldPassword = ref(props.oldPassword ?? "")
const newPassword = ref("")
const newPasswordRepeat = ref("")
const errorMessage = ref('')
const waitForResponse = ref(false)

const passwordChanged = ref(false)

async function defaultChangePassword() {
  return fetch(API_URL + "/change-password", {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      oldPassword: oldPassword.value,
      newPassword: newPassword.value
    })
  })
}

async function saveNewPassword() {
  errorMessage.value = null

  waitForResponse.value = true
  const res = await (props.changePasswordFunction ?? defaultChangePassword)(newPassword.value);
  waitForResponse.value = false

  if (res.ok) {
    console.log('Password changed')
    passwordChanged.value = true
    emit('password-changed')
  } else {
    console.warn('Failed to change password')
    errorMessage.value = await parseEloCredentialsError(res);
  }
}
</script>

<template>
  <b-loading is-full-page v-model="waitForResponse"></b-loading>

  <form id="changePasswordForm" @submit.prevent="saveNewPassword()" data-test-id="section-change_password" v-if="!passwordChanged">
    <p>{{$t('please_change_password')}}</p>
    <PasswordRules :passwordRules="props.passwordRules" v-if="props.passwordRules" />

    <template v-if="!props.oldPassword">
      <b-field :label="$t('old_password')" class="my-4" label-for="oldPassword">
        <b-input v-model="oldPassword"      autocomplete="off" type="password" password-reveal @input="errorMessage = null" id="oldPassword" name="oldPassword" autofocus></b-input>
      </b-field>
    </template>

    <b-field :label="$t('new_password')" class="mt-4" label-for="newPassword">
      <b-input v-model="newPassword"       autocomplete="off" type="password" password-reveal @input="errorMessage = null" id="newPassword" name="newPassword" autofocus></b-input>
    </b-field>
    <b-field class="mb-4">
      <password-strength :password="newPassword" />
    </b-field>

    <b-field :label="$t('new_password_repeat')" class="my-4" label-for="newPasswordRepeat">
      <b-input v-model="newPasswordRepeat" autocomplete="off" type="password" password-reveal @input="errorMessage = null" id="newPasswordRepeat" name="newPasswordRepeat"></b-input>
    </b-field>

    <section class="error-messages-placeholder">
      <div class="error-message" v-if="errorMessage">
        <b-icon icon="notification-error" class="mx-1"></b-icon>
        <span data-test-id="error-message">{{errorMessage}}</span>
      </div>
      <div class="error-message" v-if="newPassword && newPasswordRepeat && newPassword != newPasswordRepeat">
        <b-icon icon="notification-error" class="mx-1"></b-icon>
        <span data-test-id="error-message-new_password_no_match">{{$t('new_password_no_match')}}</span>
      </div>
      <!-- EAUTH2-145: Error message if new and old password are the same -->
      <div class="error-message" v-if="newPassword && newPassword == oldPassword">
        <b-icon icon="notification-error" class="mx-1"></b-icon>
        <span data-test-id="error-message-new_password_must_be_different">{{$t('new_password_must_be_different')}}</span>
      </div>
    </section>

      <b-button :disabled="!oldPassword || !newPassword || !newPasswordRepeat || newPassword != newPasswordRepeat || newPassword == oldPassword"
                native-type="submit" type="is-dark" class="mt-5" expanded
                @click.prevent="saveNewPassword()"
                data-test-id="button-change_password">
        {{$t('change_password')}}
      </b-button>
  </form>
  <div v-if="passwordChanged" class="message is-success" data-test-id="message-password_changed">
    <div class="message-body">
      {{$t('password_changed')}}
    </div>
  </div>
</template>

<style scoped>
/* EAUTH2-144/145: updated margin to stabilize unwanted resizes */
.error-messages-placeholder {
  min-height: 42px; /* EAUTH2-222: leave only enough space for a single two-liner error message */
  margin: 24px 0;
}
</style>
